import { useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { plural } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  dateTime,
  Frequency,
  NaiveDateTime,
  rrulestr,
  TimeUnits,
  Weekday
} from '@guestgoodie/helpers'

import { WeekOption } from '../types'

import {
  useFrequencyToLabel,
  useWeekdayToLabel,
  useWeekOptionToLabel
} from '../utils/helpers'
import { DAY_DURATION_MILLISECONDS } from './RecDate'

function useFrequencyIntervalLabel(freq: Frequency, interval: number = 1) {
  return useMemo(() => {
    switch (freq) {
      case Frequency.DAILY:
        return plural(interval, {
          one: `Every day`,
          other: `Every #. day`
        })
      case Frequency.WEEKLY:
        return plural(interval, {
          one: `Every week`,
          other: `Every #. week`
        })
      case Frequency.MONTHLY:
        return plural(interval, {
          one: `Every month`,
          other: `Every #. month`
        })
      case Frequency.YEARLY:
        return plural(interval, {
          one: `Every year`,
          other: `Every #. year`
        })
    }
  }, [freq, interval])
}

export function RecurrenceLabel({
  recurrence
}: {
  recurrence: GraphQL.RecurrenceFragmentFragment
}) {
  const { i18n } = useLingui()
  const rrule = useMemo(() => rrulestr(recurrence.rrule), [recurrence.rrule])

  const frequencyToLabel = useFrequencyToLabel()
  const weekdayToLabel = useWeekdayToLabel()
  const weekOptionToLabel = useWeekOptionToLabel()

  const freq = rrule.options.freq
  const interval = rrule.origOptions.interval
  const onlyOnce = rrule.origOptions.count === 1
  const until = rrule.origOptions.until
  const byweekday = rrule.origOptions.byweekday
  const bymonthday = rrule.origOptions.bymonthday
  const bymonth = rrule.origOptions.bymonth

  const intervalLabel = useFrequencyIntervalLabel(freq, interval)

  const weekdays = useMemo(() => {
    if (freq !== Frequency.WEEKLY) return null
    if (!byweekday) return null

    const weekdays = Array.isArray(byweekday) ? byweekday : [byweekday]

    return weekdays
      .map(weekday => weekdayToLabel[(weekday as Weekday).weekday])
      .join(', ')
  }, [freq, byweekday, weekdayToLabel])

  const complexWeekdays = useMemo(() => {
    if (![Frequency.MONTHLY, Frequency.YEARLY].includes(freq)) return null
    if (!byweekday) return null

    const weekdays = Array.isArray(byweekday) ? byweekday : [byweekday]

    return weekdays.map(weekday => {
      const wd = weekday as Weekday

      const weekOpt = weekOptionToLabel[wd.n as WeekOption] ?? null
      const firstString = weekOpt ? `${weekOpt} ` : ''

      const day = weekdayToLabel[(weekday as Weekday).weekday]

      return `${firstString}${day}`
    })
  }, [freq, byweekday, weekOptionToLabel, weekdayToLabel])

  const monthdays = useMemo(() => {
    if (!bymonthday) return null

    const days = Array.isArray(bymonthday) ? bymonthday : [bymonthday]

    return days.map(day => `${day}.`).join('/')
  }, [bymonthday])

  const months = useMemo(() => {
    if (!bymonth) return null

    const months = Array.isArray(bymonth) ? bymonth : [bymonth]

    return months.map(month => `${month}.`).join('/')
  }, [bymonth])

  const isWholeDay = useMemo(() => {
    const { startDate, duration } = recurrence

    const start = NaiveDateTime.fromNaiveISO(startDate)
    const day = start.toDay()

    // Check that it actually starts at 0 on the day
    if (day.diff(start, TimeUnits.MILLISECOND) !== 0) return false

    // Check that the duration actually takes the whole day
    if (duration !== DAY_DURATION_MILLISECONDS) return false
    return true
  }, [recurrence])

  const iso = useMemo(() => {
    // We first need to go through the NaiveDateTime to create a fake UTC Date that we can use with dateTime operations
    return NaiveDateTime.fromNaiveISO(recurrence.startDate)
      .toZonedDate()
      .toISOString()
  }, [recurrence.startDate])

  const untilLabel = useMemo(() => {
    return until && NaiveDateTime.fromDate(until).formatAsFull()
  }, [until])

  return (
    <div className="flex flex-col gap-2 items-start">
      <span className="text-text text-regular font-bold whitespace-nowrap">
        {dateTime.toRange({
          duration: recurrence.duration,
          isoDateTime: iso,
          showAsOffset: !onlyOnce && !isWholeDay,
          dayLabelTranslation(count) {
            return i18n._(
              plural(count, {
                1: '1 Day',
                other: '# Days'
              })
            )
          }
        })}
      </span>
      {!onlyOnce && (
        <div className="flex flex-col gap-1 items-start text-text text-small">
          <span className="text-regular">
            <Trans>Repeating</Trans>:
          </span>
          <span>{frequencyToLabel[freq]}</span>
          <span>{intervalLabel}</span>
          {weekdays && <span>{weekdays}</span>}
          {months && (
            <span>
              {months} <Trans>month</Trans>
            </span>
          )}
          {complexWeekdays && <span>{complexWeekdays}</span>}
          {monthdays && (
            <span>
              {monthdays} <Trans>day</Trans>
            </span>
          )}
          {until && (
            <span>
              <Trans>Ends at</Trans> {untilLabel}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
